import { type AddressData, type EnabledFeatures, type KYC } from 'shared/types'

import {
  type SessionWebPaymentAccount,
  type SessionWebResponse,
  type SessionWebStoneAccount,
  type StoneAccountsResponse
} from '../entities'
import {
  type ApprovalStatus,
  type Entity,
  type Kyc,
  type OptIns,
  type OriginBrand,
  type PaymentAccount,
  type Person,
  type Subject
} from '../types'

import { entityFingerprint, personFingerprint, subjectFingerprint } from './assertions'
import { deriveRoles } from './derive-roles'

interface SessionWebParserInput {
  sessionWeb: SessionWebResponse
  stoneAccounts: StoneAccountsResponse
}

const parsePaymentAccount = (
  paymentAccount: SessionWebPaymentAccount,
  owner: { id: string; name: string; document: string }
): PaymentAccount => ({
  id: paymentAccount.id,
  accountCode: paymentAccount.code,
  branchCode: paymentAccount.branchCode,
  createdAt: paymentAccount.createdAt,
  enabledFeatures: paymentAccount.featureFlags as EnabledFeatures[],
  ownerDocument: owner.document,
  ownerId: owner.id,
  ownerName: owner.name,
  ...(paymentAccount?.status ? { status: paymentAccount.status as PaymentAccount['status'] } : {})
})

const parsePersonFromStoneAccount = (stoneAccount: Omit<SessionWebStoneAccount, 'kycRequest'>): Person => ({
  [personFingerprint]: true,
  id: stoneAccount.legacy.globalId,
  document: stoneAccount.document,
  documentType: stoneAccount.documentType.toUpperCase(),
  email: stoneAccount.email,
  displayName: stoneAccount.name || 'Não informado',
  createdAt: stoneAccount.createdAt,
  approvalStatus: stoneAccount.approvalStatus as ApprovalStatus,
  roles: deriveRoles(stoneAccount.actions, stoneAccount.paymentProfiles, Boolean(stoneAccount.paymentAccount))
})

export const parseAccountList = (stoneAccounts: Array<Omit<SessionWebStoneAccount, 'kycRequest'>>) => {
  const organizations = stoneAccounts
    .filter(stoneAccount => stoneAccount.documentType?.toUpperCase() === 'CNPJ')
    .map(parsePersonFromStoneAccount)

  const users = stoneAccounts
    .filter(stoneAccount => stoneAccount.documentType?.toUpperCase() === 'CPF')
    .map(parsePersonFromStoneAccount)

  return { organizations, users, stoneAccounts: [...organizations, ...users] }
}

const parseSubject = ({ sessionWeb, stoneAccounts: stoneAccountsResponse }: SessionWebParserInput): Subject<OptIns> => {
  const { user, stoneAccount } = sessionWeb
  const { stoneAccounts } = stoneAccountsResponse

  const { organizations, users, stoneAccounts: accountList } = parseAccountList(stoneAccounts)

  const paymentAccounts = stoneAccounts.flatMap(stoneAccount =>
    stoneAccount.paymentAccount
      ? [
          parsePaymentAccount(stoneAccount.paymentAccount, {
            id: stoneAccount.id,
            name: stoneAccount.name,
            document: stoneAccount.document
          })
        ]
      : []
  )

  return {
    [subjectFingerprint]: true,
    id: user.id,
    email: user.email,
    emailVerified: true,
    phoneNumber: user.phone,
    displayName: user.name || 'Não informado',
    address: user.address as AddressData,
    document: user.document,
    kycStatus: user.kyc?.status as KYC,
    ...(user.kyc ? { kyc: user.kyc as Kyc } : {}),
    approvalStatus: user.approvalStatus as ApprovalStatus,
    paymentAccounts,
    createdAt: user.createdAt,
    actions: user.actions ?? [],
    userRoles: deriveRoles(user.actions, stoneAccount.paymentProfiles, Boolean(stoneAccount.paymentAccount)),
    isOwner: user.isOwner,
    stoneAccounts: accountList,
    unsafe: {
      fullName: '',
      legalName: '',
      hasAnActivePaymentAccount: false,
      entitiesKycRequests: [],
      roles: { acquirer: false, banking: false, credit: false },
      stoneCodes: []
    },
    enabledFeatures: user.featureFlags as EnabledFeatures[],
    featureFlags: user.featureFlags as EnabledFeatures[],
    credentials: user.credentials,
    organizations,
    users,
    kycRequest: undefined
  }
}

const parseEntity = ({ sessionWeb }: SessionWebParserInput): Entity<OptIns> => {
  const { stoneAccount, user } = sessionWeb

  return {
    [entityFingerprint]: true,
    ...parsePersonFromStoneAccount({ ...stoneAccount, actions: user.actions }),
    enabledFeatures: stoneAccount.featureFlags as EnabledFeatures[],
    featureFlags: stoneAccount.featureFlags as EnabledFeatures[],
    rejectedKycChecks: [],
    kycStatus: stoneAccount.kyc?.status as KYC,
    hasAnActivePaymentAccount: Boolean(stoneAccount.paymentAccount),
    stoneAccount: {
      id: stoneAccount.id,
      paymentProfiles: stoneAccount.paymentProfiles,
      originBrand: stoneAccount.originBrand as OriginBrand,
      situation: stoneAccount.situation,
      isOwner: user.isOwner,
      hasActiveOffer: stoneAccount.hasActiveOffer,
      ...(stoneAccount.kyc ? { kyc: stoneAccount.kyc as Kyc } : {})
    },
    paymentAccount: stoneAccount.paymentAccount
      ? parsePaymentAccount(stoneAccount.paymentAccount, {
          id: stoneAccount.id,
          name: stoneAccount.name,
          document: stoneAccount.document
        })
      : (undefined as unknown as PaymentAccount),
    stoneCodes: [],
    kycRequest: undefined
  }
}

export const sessionWebParser = ({ sessionWeb, stoneAccounts }: SessionWebParserInput) => ({
  subject: parseSubject({ sessionWeb, stoneAccounts }),
  entity: parseEntity({ sessionWeb, stoneAccounts })
})
